import type { ApiConversation } from "@/features/messaging/lib/messagingApi";
import { mapApiMessageToMailboxEmail } from "@/features/messaging/lib/messagingApi";
import {
  formatMailboxDate,
  sortEmailsNewestFirst,
  type MailboxEmail,
} from "@/features/messaging/types/mailboxEmail";

export type MailboxConversation = {
  id: string;
  status?: ApiConversation["status"];
  subject: string;
  fromName: string;
  senderRole: string;
  preview: string;
  sentAt: string;
  sentAtMs: number;
  hasUnread: boolean;
  messageCount: number;
  messages: MailboxEmail[];
};

export type MailboxFolder = "inbox" | "sent" | "closed";

function baseSubject(subject: string): string {
  return subject.replace(/^Re:\s*/i, "").trim();
}

function sortMessagesOldestFirst(messages: MailboxEmail[]): MailboxEmail[] {
  return [...messages].sort((a, b) => a.sentAtMs - b.sentAtMs);
}

export function mapApiConversationsToMailboxConversations(
  conversations: ApiConversation[],
  currentUserId: number | null,
): MailboxConversation[] {
  return conversations
    .map((conversation) => {
      const conversationId = String(conversation.pk);
      const messages = sortMessagesOldestFirst(
        conversation.messages.map((msg) =>
          mapApiMessageToMailboxEmail(msg, conversationId, currentUserId),
        ),
      );
      if (messages.length === 0) return null;

      const sortedNewest = sortEmailsNewestFirst(messages);
      const latest = sortedNewest[0];
      const oldest = messages[0];
      const latestIncoming = sortedNewest.find((m) => !m.isFromMe);

      return {
        id: conversationId,
        status: conversation.status,
        subject: baseSubject(oldest.subject),
        fromName: latestIncoming?.fromName ?? latest.fromName,
        senderRole: latestIncoming?.senderRole ?? latest.senderRole,
        preview: latest.preview,
        sentAt: latest.sentAt,
        sentAtMs: latest.sentAtMs,
        hasUnread: messages.some((m) => !m.isRead && !m.isFromMe),
        messageCount: messages.length,
        messages,
      };
    })
    .filter((c): c is MailboxConversation => c != null)
    .sort((a, b) => b.sentAtMs - a.sentAtMs);
}

export function filterConversationsByFolder(
  conversations: MailboxConversation[],
  folder: MailboxFolder,
): MailboxConversation[] {
  if (folder === "sent") {
    return conversations.filter((c) => c.messages.some((m) => m.isFromMe));
  }
  return conversations.filter((c) => c.messages.some((m) => !m.isFromMe));
}

export function countConversationsByFolder(
  conversations: MailboxConversation[],
  folder: MailboxFolder,
): number {
  return filterConversationsByFolder(conversations, folder).length;
}

export function countUnreadInbox(conversations: MailboxConversation[]): number {
  return conversations.reduce(
    (sum, c) => sum + c.messages.filter((m) => !m.isRead && !m.isFromMe).length,
    0,
  );
}

function rebuildConversationMetadata(
  messages: MailboxEmail[],
  id: string,
  subject?: string,
): MailboxConversation {
  const sortedOldest = sortMessagesOldestFirst(messages);
  const sortedNewest = sortEmailsNewestFirst(messages);
  const latest = sortedNewest[0];
  const oldest = sortedOldest[0];
  const latestIncoming = sortedNewest.find((m) => !m.isFromMe);

  return {
    id,
    subject: baseSubject(subject ?? oldest?.subject ?? latest?.subject ?? ""),
    fromName: latestIncoming?.fromName ?? latest?.fromName ?? "",
    senderRole: latestIncoming?.senderRole ?? latest?.senderRole ?? "",
    preview: latest?.preview ?? "",
    sentAt: latest?.sentAt ?? "",
    sentAtMs: latest?.sentAtMs ?? 0,
    hasUnread: messages.some((m) => !m.isRead && !m.isFromMe),
    messageCount: messages.length,
    messages: sortedOldest,
  };
}

function mergeConversationPair(
  a: MailboxConversation,
  b: MailboxConversation,
): MailboxConversation {
  const byId = new Map<string, MailboxEmail>();
  for (const message of [...a.messages, ...b.messages]) {
    byId.set(message.id, { ...message, conversationId: a.id });
  }
  return rebuildConversationMetadata(
    sortMessagesOldestFirst([...byId.values()]),
    a.id,
    a.subject || b.subject,
  );
}

/** Remplace les ids optimistes (UUID) par les ids serveur après messageSent. */
export function reconcileMessageSentAck(
  conversations: MailboxConversation[],
  ack: {
    clientMessageId?: string | number;
    messageId?: string | number;
    conversationId?: string | number;
  },
): MailboxConversation[] {
  const clientId = ack.clientMessageId != null ? String(ack.clientMessageId).trim() : "";
  const serverMessageId = ack.messageId != null ? String(ack.messageId) : "";
  const serverConversationId =
    ack.conversationId != null ? String(ack.conversationId) : "";
  if (!clientId || !serverMessageId || !serverConversationId) return conversations;

  const sourceIdx = conversations.findIndex(
    (c) => c.id === clientId || c.messages.some((m) => m.id === clientId),
  );
  if (sourceIdx < 0) return conversations;

  const source = conversations[sourceIdx];
  const updatedMessages = source.messages.map((message) =>
    message.id === clientId
      ? { ...message, id: serverMessageId, conversationId: serverConversationId }
      : { ...message, conversationId: serverConversationId },
  );
  const updatedSource = rebuildConversationMetadata(
    updatedMessages,
    serverConversationId,
    source.subject,
  );

  const targetIdx = conversations.findIndex(
    (c, idx) => idx !== sourceIdx && c.id === serverConversationId,
  );

  if (targetIdx < 0) {
    const next = [...conversations];
    next[sourceIdx] = updatedSource;
    return next.sort((a, b) => b.sentAtMs - a.sentAtMs);
  }

  const merged = mergeConversationPair(conversations[targetIdx], updatedSource);
  return conversations
    .filter((_, idx) => idx !== sourceIdx && idx !== targetIdx)
    .concat(merged)
    .sort((a, b) => b.sentAtMs - a.sentAtMs);
}

export function upsertMessageInConversations(
  conversations: MailboxConversation[],
  email: MailboxEmail,
): MailboxConversation[] {
  const existing = conversations.find((c) => c.id === email.conversationId);
  if (existing) {
    if (existing.messages.some((m) => m.id === email.id)) return conversations;
    const messages = sortMessagesOldestFirst([...existing.messages, email]);
    const latest = sortEmailsNewestFirst(messages)[0];
    const latestIncoming = sortEmailsNewestFirst(messages).find((m) => !m.isFromMe);
    return conversations
      .map((c) =>
        c.id === email.conversationId
          ? {
              ...c,
              messages,
              preview: latest.preview,
              sentAt: latest.sentAt,
              sentAtMs: latest.sentAtMs,
              fromName: latestIncoming?.fromName ?? latest.fromName,
              senderRole: latestIncoming?.senderRole ?? latest.senderRole,
              hasUnread: messages.some((m) => !m.isRead && !m.isFromMe),
              messageCount: messages.length,
            }
          : c,
      )
      .sort((a, b) => b.sentAtMs - a.sentAtMs);
  }

  const messages = [email];
  const newConversation: MailboxConversation = {
    id: email.conversationId,
    subject: baseSubject(email.subject),
    fromName: email.isFromMe ? email.replyToName ?? email.fromName : email.fromName,
    senderRole: email.senderRole,
    preview: email.preview,
    sentAt: email.sentAt,
    sentAtMs: email.sentAtMs,
    hasUnread: !email.isRead && !email.isFromMe,
    messageCount: 1,
    messages,
  };
  return [newConversation, ...conversations].sort((a, b) => b.sentAtMs - a.sentAtMs);
}

export function replyTargetForConversation(
  conversation: MailboxConversation,
): {
  senderUserId?: number;
  fromName: string;
  fromEmail: string;
  recipientUserId?: number;
  replyToName?: string;
  replyToEmail?: string;
} | null {
  const sorted = sortMessagesOldestFirst(conversation.messages);
  const last = sorted[sorted.length - 1];
  if (!last) return null;

  if (last.isFromMe) {
    return {
      ...(last.senderUserId != null ? { senderUserId: last.senderUserId } : {}),
      fromName: last.replyToName ?? last.fromName,
      fromEmail: last.replyToEmail ?? last.fromEmail,
      ...(last.recipientUserId != null ? { recipientUserId: last.recipientUserId } : {}),
      ...(last.replyToName != null ? { replyToName: last.replyToName } : {}),
      ...(last.replyToEmail != null ? { replyToEmail: last.replyToEmail } : {}),
    };
  }

  return {
    ...(last.senderUserId != null ? { senderUserId: last.senderUserId } : {}),
    fromName: last.fromName,
    fromEmail: last.fromEmail,
    ...(last.senderUserId != null ? { recipientUserId: last.senderUserId } : {}),
    replyToName: last.fromName,
    replyToEmail: last.fromEmail,
  };
}

export function buildOutgoingEmail(params: {
  messageId: string;
  conversationId: string;
  subject: string;
  body: string;
  fromName: string;
  fromEmail: string;
  senderUserId?: number;
  recipientUserId?: number;
  replyToName?: string;
  replyToEmail?: string;
}): MailboxEmail {
  const sentAtMs = Date.now();
  return {
    id: params.messageId,
    conversationId: params.conversationId,
    subject: params.subject,
    fromName: params.fromName,
    fromEmail: params.fromEmail,
    senderRole: "Envoyé",
    body: params.body,
    preview: params.body.slice(0, 120),
    sentAt: formatMailboxDate(new Date(sentAtMs)),
    sentAtMs,
    isRead: true,
    isFromMe: true,
    ...(params.senderUserId != null ? { senderUserId: params.senderUserId } : {}),
    ...(params.recipientUserId != null ? { recipientUserId: params.recipientUserId } : {}),
    ...(params.replyToName != null ? { replyToName: params.replyToName } : {}),
    ...(params.replyToEmail != null ? { replyToEmail: params.replyToEmail } : {}),
  };
}
